import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { trackVideoPlay } from '../../../services/userJourneyService';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isObjFile = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url?.toLowerCase()?.split('?')?.[0]?.endsWith('.obj');
};

const isGlbFile = (url) => {
  if (!url || typeof url !== 'string') return false;
  const path = url?.toLowerCase()?.split('?')?.[0];
  return path?.endsWith('.glb') || path?.endsWith('.gltf');
};

const is3DModelFile = (url) => isObjFile(url) || isGlbFile(url);

const getYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url?.match(pattern);
    if (match) return match?.[1];
  }
  return null;
};

const getVimeoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const match = url?.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match?.[1] : null;
};

const getEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const ytId = getYouTubeId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`;
  const vimeoId = getVimeoId(url);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
  return null;
};

const isEmbeddable = (url) => !!getEmbedUrl(url);

// ─── Inline Iframe Player (YouTube / Vimeo) ──────────────────────────────────
const IframeVideoPlayer = ({ url, property, currentVideoIndex, totalVideos, onPrev, onNext }) => {
  const [playing, setPlaying] = useState(false);
  const embedUrl = getEmbedUrl(url);

  return (
    <div className="relative bg-black rounded-xl overflow-hidden shadow-deep">
      <div className="w-full h-[400px] md:h-[500px] lg:h-[600px] relative">
        {!playing ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            {property?.images?.[0] && (
              <img
                src={property?.images?.[0]}
                alt={property?.title || 'Aperçu vidéo'}
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
            )}
            <div className="absolute inset-0 bg-black/40" />
            <button
              onClick={() => setPlaying(true)}
              className="relative z-10 w-20 h-20 bg-brand-gold hover:bg-brand-gold/90 rounded-full flex items-center justify-center shadow-2xl transition-transform duration-200 hover:scale-110"
              aria-label="Lire la vidéo"
            >
              <Icon name="Play" size={36} color="black" strokeWidth={2.5} className="ml-1" />
            </button>
            <div className="absolute top-4 left-4 right-4 z-10">
              <div className="bg-black/60 backdrop-blur-sm rounded-lg p-3">
                <h3 className="text-white font-heading font-heading-bold text-base">
                  Visite Virtuelle — {property?.title}
                </h3>
                <p className="text-white/70 text-xs mt-0.5">
                  Cliquez sur ▶ pour démarrer la lecture
                </p>
              </div>
            </div>
          </div>
        ) : (
          <iframe
            src={embedUrl}
            title={property?.title || 'Visite virtuelle'}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        )}
      </div>
      {totalVideos > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center space-x-4 pointer-events-none">
          <button
            onClick={() => { setPlaying(false); onPrev(); }}
            className="pointer-events-auto text-white bg-black/60 hover:bg-black/80 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors"
          >
            <Icon name="SkipBack" size={16} />
          </button>
          <span className="pointer-events-auto text-white text-sm bg-black/60 px-3 py-1 rounded-full">
            {currentVideoIndex + 1} / {totalVideos}
          </span>
          <button
            onClick={() => { setPlaying(false); onNext(); }}
            className="pointer-events-auto text-white bg-black/60 hover:bg-black/80 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors"
          >
            <Icon name="SkipForward" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Inline Three.js OBJ Viewer ───────────────────────────────────────────────
const ObjModelViewer = ({ url, property, currentVideoIndex, totalVideos, onPrev, onNext }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const animFrameRef = useRef(null);
  const modelRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const rotationRef = useRef({ x: 0.3, y: 0 });
  const zoomRef = useRef(5);
  // Touch pinch-zoom tracking
  const lastPinchDistRef = useRef(null);
  // Quality badge label
  const [qualityLabel, setQualityLabel] = useState('');

  const [viewerState, setViewerState] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [loadProgress, setLoadProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHints, setShowHints] = useState(true);
  const hintsTimerRef = useRef(null);

  // ── Device / bandwidth detection helpers ──────────────────────────────────
  const isMobileDevice = () => {
    return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i?.test(navigator?.userAgent) ||
      (window?.innerWidth <= 768);
  };

  /**
   * Returns a quality tier: 'low' | 'medium' | 'high'
   * Based on navigator.connection (Network Information API) + device type.
   */
  const detectQualityTier = () => {
    const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    const mobile = isMobileDevice();

    if (conn) {
      const { effectiveType, saveData, downlink } = conn;
      if (saveData) return 'low';
      if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'low';
      if (effectiveType === '3g' || (downlink && downlink < 2)) return mobile ? 'low' : 'medium';
      if (effectiveType === '4g' && mobile) return 'medium';
    }
    // Fallback: mobile without connection API → medium; desktop → high
    return mobile ? 'medium' : 'high';
  };

  /**
   * Simplify geometry by merging close vertices (LOD reduction).
   * On low/medium tier we reduce float precision of vertex positions.
   */
  const applyLOD = (object, THREE, tier) => {
    if (tier === 'high') return; // No LOD reduction on desktop/fast connections
    object?.traverse((child) => {
      if (!child?.isMesh || !child?.geometry) return;
      const geo = child?.geometry;
      const pos = geo?.attributes?.position;
      if (!pos) return;
      // Round vertex positions to reduce effective polygon density
      const precision = tier === 'low' ? 1e2 : 1e3; // low=2dp, medium=3dp
      const arr = pos?.array;
      for (let i = 0; i < arr?.length; i++) {
        arr[i] = Math.round(arr?.[i] * precision) / precision;
      }
      pos.needsUpdate = true;
      // Recompute normals after vertex modification
      try { geo?.computeVertexNormals(); } catch (_) {}
    });
  };

  const cleanup = useCallback(() => {
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);
    if (rendererRef?.current) {
      rendererRef?.current?.dispose();
      rendererRef.current = null;
    }
    sceneRef.current = null;
    cameraRef.current = null;
    modelRef.current = null;
  }, []);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  // Handle fullscreen change events
  useEffect(() => {
    const onFsChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement);
      setIsFullscreen(isFull);
      // Resize renderer to match new dimensions
      setTimeout(() => {
        const container = containerRef?.current;
        if (!container || !rendererRef?.current || !cameraRef?.current) return;
        const w = container?.getBoundingClientRect()?.width || container?.offsetWidth;
        const h = container?.getBoundingClientRect()?.height || container?.offsetHeight;
        if (w > 0 && h > 0) {
          rendererRef?.current?.setSize(w, h, false);
          cameraRef.current.aspect = w / h;
          cameraRef?.current?.updateProjectionMatrix();
        }
      }, 150);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
    };
  }, []);

  // ResizeObserver — keep renderer in sync with container on all screen sizes
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      if (!rendererRef?.current || !cameraRef?.current) return;
      const w = container.getBoundingClientRect().width || container.offsetWidth;
      const h = container.getBoundingClientRect().height || container.offsetHeight;
      if (w > 0 && h > 0) {
        rendererRef.current.setSize(w, h, false);
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
      }
    });
    ro?.observe(container);
    return () => ro?.disconnect();
  }, []);

  // ── Non-passive touch event listeners (needed for preventDefault) ─────────
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    const onTouchStart = (e) => {
      // If the touch target is the fullscreen button or its child, skip drag logic
      const fsBtn = e?.target?.closest?.('button[aria-label]');
      if (fsBtn) return;
      if (e?.touches?.length === 1) {
        isDraggingRef.current = true;
        lastMouseRef.current = { x: e?.touches?.[0]?.clientX, y: e?.touches?.[0]?.clientY };
        lastPinchDistRef.current = null;
      } else if (e?.touches?.length === 2) {
        isDraggingRef.current = false;
        const dx = e?.touches?.[0]?.clientX - e?.touches?.[1]?.clientX;
        const dy = e?.touches?.[0]?.clientY - e?.touches?.[1]?.clientY;
        lastPinchDistRef.current = Math.hypot(dx, dy);
      }
    };

    const onTouchMove = (e) => {
      if (e?.touches?.length === 1 && isDraggingRef?.current && modelRef?.current) {
        e?.preventDefault(); // prevent page scroll while orbiting
        const dx = e?.touches?.[0]?.clientX - lastMouseRef?.current?.x;
        const dy = e?.touches?.[0]?.clientY - lastMouseRef?.current?.y;
        modelRef.current.rotation.y += dx * 0.012;
        modelRef.current.rotation.x += dy * 0.012;
        lastMouseRef.current = { x: e?.touches?.[0]?.clientX, y: e?.touches?.[0]?.clientY };
      } else if (e?.touches?.length === 2 && cameraRef?.current) {
        e?.preventDefault(); // prevent pinch-zoom on the page
        const dx = e?.touches?.[0]?.clientX - e?.touches?.[1]?.clientX;
        const dy = e?.touches?.[0]?.clientY - e?.touches?.[1]?.clientY;
        const dist = Math.hypot(dx, dy);
        if (lastPinchDistRef?.current !== null) {
          const delta = lastPinchDistRef?.current - dist;
          zoomRef.current = Math.max(0.5, Math.min(20, zoomRef?.current + delta * 0.03));
          cameraRef.current.position.z = zoomRef?.current;
        }
        lastPinchDistRef.current = dist;
      }
    };

    const onTouchEnd = () => {
      isDraggingRef.current = false;
      lastPinchDistRef.current = null;
    };

    // Must be non-passive to allow preventDefault
    el?.addEventListener('touchstart', onTouchStart, { passive: false });
    el?.addEventListener('touchmove', onTouchMove, { passive: false });
    el?.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el?.removeEventListener('touchstart', onTouchStart);
      el?.removeEventListener('touchmove', onTouchMove);
      el?.removeEventListener('touchend', onTouchEnd);
    };
  }, [viewerState]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef?.current;
    if (!el) return;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (el?.requestFullscreen) el?.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el?.webkitRequestFullscreen();
      else if (el?.mozRequestFullScreen) el?.mozRequestFullScreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    }
  }, []);

  const startViewer = useCallback(async () => {
    setViewerState('loading');
    setLoadProgress(0);
    setErrorMsg('');
    setShowHints(true);

    try {
      const THREE = await import('three');
      const container = containerRef?.current;
      const canvas = canvasRef?.current;
      if (!container || !canvas) throw new Error('Canvas non disponible');

      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const width = container?.getBoundingClientRect()?.width || container?.offsetWidth || 800;
      const height = container?.getBoundingClientRect()?.height || container?.offsetHeight || 500;

      const tier = detectQualityTier();
      const tierLabels = { low: 'Qualité réduite', medium: 'Qualité standard', high: 'Haute qualité' };
      setQualityLabel(tierLabels?.[tier]);

      const maxDPR = tier === 'low' ? 1 : tier === 'medium' ? 1.5 : 2;
      const dpr = Math.min(window.devicePixelRatio || 1, maxDPR);
      const useAntialias = tier === 'high';

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x1a1a2e);
      sceneRef.current = scene;

      scene?.add(new THREE.AmbientLight(0xffffff, 1.0));
      const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
      dirLight?.position?.set(5, 10, 7);
      scene?.add(dirLight);
      const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
      dirLight2?.position?.set(-5, -5, -5);
      scene?.add(dirLight2);
      if (tier !== 'low') {
        const dirLight3 = new THREE.DirectionalLight(0xffffff, 0.4);
        dirLight3?.position?.set(0, -10, 0);
        scene?.add(dirLight3);
      }

      const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000);
      camera?.position?.set(0, 0, zoomRef?.current);
      cameraRef.current = camera;

      const renderer = new THREE.WebGLRenderer({ canvas, antialias: useAntialias, alpha: false });
      renderer?.setSize(width, height, false);
      renderer?.setPixelRatio(dpr);
      renderer.shadowMap.enabled = tier === 'high';
      renderer.outputColorSpace = THREE?.SRGBColorSpace;
      renderer.toneMapping = THREE?.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.0;
      rendererRef.current = renderer;

      setLoadProgress(20);

      let object;

      if (isGlbFile(url)) {
        // ── Load GLB/GLTF with GLTFLoader ──────────────────────────────────
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        const loader = new GLTFLoader();

        const gltf = await new Promise((resolve, reject) => {
          loader.load(
            url,
            (g) => resolve(g),
            (xhr) => {
              if (xhr.lengthComputable) {
                setLoadProgress(20 + Math.round((xhr.loaded / xhr.total) * 70));
              }
            },
            (err) => reject(err)
          );
        });

        object = gltf?.scene;

        // Enable shadows and fix materials for GLB
        object?.traverse((child) => {
          if (child?.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            if (child?.material) {
              const mats = Array.isArray(child?.material) ? child?.material : [child?.material];
              mats?.forEach(mat => {
                if (mat) {
                  mat.side = THREE?.DoubleSide;
                  if (child?.geometry?.attributes?.color) mat.vertexColors = true;
                }
              });
            }
          }
        });

        // Play first animation if present
        if (gltf?.animations?.length > 0) {
          const { AnimationMixer } = THREE;
          const mixer = new AnimationMixer(object);
          mixer?.clipAction(gltf?.animations?.[0])?.play();
          // Store mixer on renderer for animation loop
          renderer._glbMixer = mixer;
          renderer._glbClock = new THREE.Clock();
        }

      } else {
        // ── Load OBJ (existing logic) ──────────────────────────────────────
        const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
        const loader = new OBJLoader();

        try {
          const { MTLLoader } = await import('three/examples/jsm/loaders/MTLLoader.js');
          const mtlUrl = url?.replace(/\.obj(\?.*)?$/i, (_, qs) => `.mtl${qs || ''}`);
          const mtlLoader = new MTLLoader();
          const materials = await new Promise((resolve, reject) => {
            mtlLoader.load(mtlUrl, resolve, undefined, reject);
          });
          materials?.preload();
          loader?.setMaterials(materials);
        } catch {
          // MTL not available
        }

        object = await new Promise((resolve, reject) => {
          loader.load(
            url,
            (obj) => resolve(obj),
            (xhr) => {
              if (xhr.lengthComputable) {
                setLoadProgress(20 + Math.round((xhr.loaded / xhr.total) * 70));
              }
            },
            (err) => reject(err)
          );
        });

        applyLOD(object, THREE, tier);

        object?.traverse((child) => {
          if (child?.isMesh) {
            if (!child?.material) {
              child.material = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                vertexColors: child?.geometry?.attributes?.color ? true : false,
                side: THREE.DoubleSide,
              });
            } else if (Array.isArray(child?.material)) {
              child?.material?.forEach(mat => {
                if (mat) {
                  mat.side = THREE?.DoubleSide;
                  if (child?.geometry?.attributes?.color) mat.vertexColors = true;
                }
              });
            } else {
              child.material.side = THREE?.DoubleSide;
              if (child?.geometry?.attributes?.color) child.material.vertexColors = true;
            }
          }
        });
      }

      setLoadProgress(95);

      // Center and scale model (same for both OBJ and GLB)
      const box = new THREE.Box3()?.setFromObject(object);
      const center = box?.getCenter(new THREE.Vector3());
      const size = box?.getSize(new THREE.Vector3());
      const maxDim = Math.max(size?.x, size?.y, size?.z);
      const scale = 3 / maxDim;
      object?.scale?.setScalar(scale);
      object?.position?.sub(center?.multiplyScalar(scale));

      scene?.add(object);
      modelRef.current = object;
      zoomRef.current = maxDim * scale * 2.5;
      camera?.position?.set(0, 0, zoomRef?.current);

      setLoadProgress(100);
      setViewerState('ready');

      if (hintsTimerRef?.current) clearTimeout(hintsTimerRef?.current);
      hintsTimerRef.current = setTimeout(() => setShowHints(false), 5000);

      let lastFrameTime = 0;
      const targetInterval = tier === 'low' ? 1000 / 30 : 0;
      const animate = (timestamp) => {
        animFrameRef.current = requestAnimationFrame(animate);
        if (targetInterval > 0 && timestamp - lastFrameTime < targetInterval) return;
        lastFrameTime = timestamp;
        // Update GLB animation mixer if present
        if (renderer?._glbMixer && renderer?._glbClock) {
          renderer?._glbMixer?.update(renderer?._glbClock?.getDelta());
        }
        renderer?.render(scene, camera);
      };
      animate(0);

    } catch (err) {
      console.error('3D model load error:', err);
      setErrorMsg('Impossible de charger le modèle 3D. Vérifiez que le fichier est accessible.');
      setViewerState('error');
    }
  }, [url]);

  // Cleanup hints timer on unmount
  useEffect(() => {
    return () => {
      if (hintsTimerRef?.current) clearTimeout(hintsTimerRef?.current);
    };
  }, []);

  // Auto-start on mount
  useEffect(() => {
    startViewer();
  }, [startViewer]);

  // Mouse orbit controls (desktop)
  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    lastMouseRef.current = { x: e?.clientX, y: e?.clientY };
  };
  const handleMouseMove = (e) => {
    if (!isDraggingRef?.current || !modelRef?.current) return;
    const dx = e?.clientX - lastMouseRef?.current?.x;
    const dy = e?.clientY - lastMouseRef?.current?.y;
    modelRef.current.rotation.y += dx * 0.01;
    modelRef.current.rotation.x += dy * 0.01;
    lastMouseRef.current = { x: e?.clientX, y: e?.clientY };
  };
  const handleMouseUp = () => { isDraggingRef.current = false; };

  const handleWheel = (e) => {
    if (!cameraRef?.current) return;
    e?.preventDefault();
    zoomRef.current = Math.max(0.5, Math.min(20, zoomRef?.current + e?.deltaY * 0.01));
    cameraRef.current.position.z = zoomRef?.current;
  };

  return (
    <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-deep h-[400px] md:h-[500px] lg:h-[600px]">
      {/* Canvas container */}
      <div
        ref={containerRef}
        className="absolute inset-0"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ cursor: viewerState === 'ready' ? (isDraggingRef?.current ? 'grabbing' : 'grab') : 'default', touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            opacity: viewerState === 'loading' ? 0 : 1,
            pointerEvents: viewerState === 'loading' ? 'none' : 'auto',
          }}
        />
      </div>
      {/* Loading state */}
      {viewerState === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
          <div className="text-center px-6">
            {/* Spinner rings */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-brand-gold/10" />
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-brand-gold animate-spin" />
              <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-brand-gold/40 animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                </svg>
              </div>
            </div>
            <p className="text-white font-semibold mb-1">
              {loadProgress < 20 ? 'Initialisation du moteur 3D…' : loadProgress < 90 ? 'Chargement du modèle 3D…' : 'Finalisation…'}
            </p>
            <p className="text-white/40 text-xs mb-4">
              {loadProgress < 20 ? 'Préparation de la scène' : loadProgress < 90 ? `${loadProgress}% téléchargé` : 'Optimisation du rendu'}
            </p>
            <div className="w-52 h-1.5 bg-white/10 rounded-full mx-auto overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-brand-gold/70 to-brand-gold rounded-full transition-all duration-300"
                style={{ width: `${loadProgress}%` }}
              />
            </div>
            <p className="text-white/30 text-xs mt-2">{loadProgress}%</p>
          </div>
        </div>
      )}
      {/* Error state */}
      {viewerState === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black px-6">
          {/* Background decorative glow */}
          <div
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 50% 40%, #C9A84C 0%, transparent 60%)' }}
          />

          <div className="relative z-10 w-full max-w-sm mx-auto">
            {/* Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm shadow-2xl text-center">
              {/* Icon with pulse ring */}
              <div className="relative w-16 h-16 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full bg-red-500/10 animate-ping" style={{ animationDuration: '2s' }} />
                <div className="relative w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={28} className="text-red-400" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-white font-heading font-heading-bold text-lg mb-1">
                Modèle 3D indisponible
              </h3>

              {/* Filename */}
              <p className="text-white/40 text-xs mb-3 font-mono truncate px-2" title={url}>
                {url ? url?.split('/')?.pop()?.split('?')?.[0] : 'fichier.obj'}
              </p>

              {/* Error message */}
              <p className="text-white/60 text-sm mb-6 leading-relaxed">
                {errorMsg || 'Le fichier .obj n\'a pas pu être chargé. Vérifiez que le fichier est accessible et réessayez.'}
              </p>

              {/* Action buttons */}
              <div className="flex flex-col gap-3">
                {/* Retry button */}
                <button
                  onClick={startViewer}
                  className="w-full inline-flex items-center justify-center space-x-2 bg-brand-gold hover:bg-brand-gold/90 active:scale-95 text-black font-semibold px-5 py-3 rounded-xl transition-all duration-200 shadow-lg"
                >
                  <Icon name="RefreshCw" size={17} strokeWidth={2.5} />
                  <span>Réessayer le chargement</span>
                </button>

                {/* Download link */}
                <a
                  href={url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center space-x-2 bg-white/8 hover:bg-white/15 active:scale-95 text-white/80 hover:text-white font-medium px-5 py-3 rounded-xl border border-white/10 hover:border-white/20 transition-all duration-200"
                >
                  <Icon name="Download" size={17} strokeWidth={2} />
                  <span>Télécharger le fichier .obj</span>
                </a>
              </div>

              {/* Help hint */}
              <p className="text-white/30 text-xs mt-4 leading-relaxed">
                Vous pouvez ouvrir le fichier téléchargé avec un logiciel 3D (Blender, MeshLab, etc.)
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Ready state overlay — controls hint + fullscreen + quality badge */}
      {viewerState === 'ready' && (
        <div className="absolute top-4 left-4 right-4 pointer-events-none">
          <div className="bg-black/60 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              </svg>
              <div>
                <p className="text-white font-heading font-heading-bold text-sm">Visite 3D Interactive — {property?.title}</p>
                <p className="text-white/60 text-xs hidden sm:block">Glisser pour pivoter · Molette pour zoomer</p>
                <p className="text-white/60 text-xs sm:hidden">1 doigt: pivoter · 2 doigts: zoomer</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 pointer-events-auto">
              {/* Adaptive quality badge */}
              {qualityLabel && (
                <span className="hidden sm:inline-flex items-center text-white/50 text-[10px] bg-white/10 px-2 py-0.5 rounded-full border border-white/10">
                  {qualityLabel}
                </span>
              )}
              {/* Fullscreen / immersion button */}
              <button
                onClick={toggleFullscreen}
                className="text-white/80 hover:text-brand-gold bg-white/10 hover:bg-white/20 transition-all duration-200 rounded-lg p-2"
                aria-label={isFullscreen ? 'Quitter le mode immersif' : 'Mode immersif plein écran'}
                title={isFullscreen ? 'Quitter le mode immersif' : 'Mode immersif plein écran'}
              >
                <Icon name={isFullscreen ? 'Minimize' : 'Maximize'} size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Orbit controls hints — auto-dismiss after 5s */}
      {viewerState === 'ready' && showHints && (
        <div
          className="absolute bottom-16 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{ animation: 'fadeInUp 0.4s ease forwards' }}
        >
          <div className="bg-black/70 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center space-x-4 border border-white/10 shadow-xl">
            {/* Desktop hint */}
            <div className="hidden sm:flex items-center space-x-2">
              <div className="flex flex-col items-center space-y-0.5">
                <div className="w-5 h-5 rounded border border-white/40 bg-white/10 flex items-center justify-center">
                  <Icon name="MousePointer2" size={11} className="text-white/70" />
                </div>
                <span className="text-white/50 text-[10px]">Glisser</span>
              </div>
              <span className="text-white/30 text-xs">·</span>
              <div className="flex flex-col items-center space-y-0.5">
                <div className="w-5 h-5 rounded border border-white/40 bg-white/10 flex items-center justify-center">
                  <Icon name="ScrollText" size={11} className="text-white/70" />
                </div>
                <span className="text-white/50 text-[10px]">Zoom</span>
              </div>
            </div>
            {/* Mobile hint */}
            <div className="flex sm:hidden items-center space-x-2">
              <div className="flex flex-col items-center space-y-0.5">
                <Icon name="Hand" size={14} className="text-white/60" />
                <span className="text-white/50 text-[10px]">1 doigt</span>
              </div>
              <span className="text-white/30 text-xs">·</span>
              <div className="flex flex-col items-center space-y-0.5">
                <Icon name="ZoomIn" size={14} className="text-white/60" />
                <span className="text-white/50 text-[10px]">Pincer</span>
              </div>
            </div>
            <div className="w-px h-6 bg-white/15" />
            <p className="text-white/50 text-xs">
              <span className="hidden sm:inline">Cliquez et glissez pour explorer</span>
              <span className="inline sm:hidden">Touchez pour explorer</span>
            </p>
          </div>
        </div>
      )}
      {/* Navigation between multiple videos */}
      {totalVideos > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center space-x-4">
          <button onClick={() => { cleanup(); setViewerState('loading'); onPrev(); }} className="text-white bg-black/50 hover:bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors">
            <Icon name="SkipBack" size={16} />
          </button>
          <span className="text-white text-sm bg-black/50 px-3 py-1 rounded-full">{currentVideoIndex + 1} / {totalVideos}</span>
          <button onClick={() => { cleanup(); setViewerState('loading'); onNext(); }} className="text-white bg-black/50 hover:bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors">
            <Icon name="SkipForward" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const PropertyVideoPlayer = ({ property }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const videoRef = useRef(null);
  const playPromiseRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Fallback 3D model URL — shown when the property has no videos
  const FALLBACK_OBJ_URL =
    'https://rwtsvvpklmosipnatufe.supabase.co/storage/v1/object/sign/LOKOI%20YOPOUGON/Scaniverse_2026_05_18_105328.obj?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9lM2I2N2NhZi05ZjM3LTRkNWYtYTFkYi1mZWZkNWM4ZjYzZjgiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJMT0tPSSBZT1BPVUdPTi9TY2FuaXZlcnNlXzIwMjZfMDVfMThfMTA1MzI4Lm9iaiIsImlhdCI6MTc3OTE0MjEzMiwiZXhwIjoxODEwNjc4MTMyfQ.J4JgBG6e6v4gHyLTJFSDLg35sArqlpZZqUDOhb5NMG8';

  const videos = (() => {
    if (!property?.videos) return [];
    if (Array.isArray(property?.videos)) return property?.videos;
    if (typeof property?.videos === 'object') return [property?.videos];
    return [];
  })();

  const hasVideos = Array.isArray(videos) && videos?.length > 0;
  const maxRetries = 3;

  useEffect(() => {
    const loadVideoUrl = async () => {
      if (!hasVideos) {
        setVideoUrl(FALLBACK_OBJ_URL);
        setVideoError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setVideoError(null);

        const currentVideo = videos?.[currentVideoIndex];
        let url = null;
        let videoPath = currentVideo?.url || currentVideo?.path || currentVideo;

        if (videoPath) {
          if (typeof videoPath !== 'string') {
            setVideoUrl(null);
            setVideoError(null);
            setLoading(false);
            return;
          }

          // Block non-video/non-3D formats
          const blockedExtensions = ['.fbx', '.stl', '.dae', '.3ds', '.blend'];
          const pathLower = videoPath?.toLowerCase()?.split('?')?.[0];
          const isBlocked = blockedExtensions?.some(ext => pathLower?.endsWith(ext));
          if (isBlocked) {
            setVideoUrl(null);
            setVideoError('Format non supporté: les fichiers .fbx, .stl et autres modèles 3D non-GLTF ne peuvent pas être lus.');
            setLoading(false);
            return;
          }

          const nonVideoExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.rar', '.txt', '.csv'];
          if (nonVideoExtensions?.some(ext => pathLower?.endsWith(ext))) {
            setVideoUrl(null);
            setVideoError(null);
            setLoading(false);
            return;
          }

          if (videoPath?.startsWith('http')) {
            url = videoPath;
          } else {
            const bucketsToTry = ['property-videos', 'property-assets', 'communes images', 'plateau', 'Avataaer'];
            for (const bucketName of bucketsToTry) {
              try {
                if (bucketName === 'property-videos' || bucketName === 'property-assets') {
                  const { data } = supabase?.storage?.from(bucketName)?.getPublicUrl(videoPath);
                  if (data?.publicUrl) { url = data?.publicUrl; break; }
                } else {
                  const { data, error } = await supabase?.storage?.from(bucketName)?.createSignedUrl(videoPath, 7200);
                  if (data?.signedUrl && !error) { url = data?.signedUrl; break; }
                }
              } catch { continue; }
            }
          }
        }

        if (url) {
          setVideoUrl(url);
          setVideoError(null);
          setRetryCount(0);
        } else {
          throw new Error('Impossible de charger la vidéo depuis le stockage');
        }
      } catch (error) {
        setVideoError(`Erreur de chargement: ${error?.message || 'URL non accessible'}`);
      } finally {
        setLoading(false);
      }
    };

    loadVideoUrl();
  }, [hasVideos, currentVideoIndex, videos]);

  const retryVideoLoad = async () => {
    if (retryCount >= maxRetries || isRetrying) return;
    setIsRetrying(true);
    await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
    setRetryCount(prev => prev + 1);
    setVideoError(null);
    setIsRetrying(false);
    const currentIndex = currentVideoIndex;
    setCurrentVideoIndex(-1);
    setTimeout(() => setCurrentVideoIndex(currentIndex), 100);
  };

  useEffect(() => {
    return () => {
      if (playPromiseRef?.current) playPromiseRef?.current?.catch(() => {});
      if (videoRef?.current && !videoRef?.current?.paused) videoRef?.current?.pause();
    };
  }, []);

  // Cancel pending play when videoUrl changes to avoid "play() interrupted" error
  useEffect(() => {
    const cancelPlay = async () => {
      const video = videoRef?.current;
      // Pause synchronously first — before awaiting the promise — so the
      // browser doesn't try to play a video element that is about to be removed
      if (video && !video?.paused) {
        try { video?.pause(); } catch (_) {}
      }
      if (playPromiseRef?.current) {
        await playPromiseRef?.current?.catch(() => {});
        playPromiseRef.current = null;
      }
      setIsPlaying(false);
    };
    cancelPlay();
  }, [videoUrl]);

  const togglePlay = async () => {
    if (!videoRef?.current || videoError || !videoUrl) return;
    try {
      if (isPlaying) {
        if (playPromiseRef?.current) {
          await playPromiseRef?.current?.catch(() => {});
          playPromiseRef.current = null;
        }
        videoRef?.current?.pause();
        setIsPlaying(false);
      } else {
        playPromiseRef.current = videoRef?.current?.play();
        await playPromiseRef?.current;
        setIsPlaying(true);
        playPromiseRef.current = null;
        // Track video play event
        trackVideoPlay(property, {
          url: videoUrl,
          duration: videoRef?.current?.duration,
          type: 'native',
        });
      }
    } catch (error) {
      playPromiseRef.current = null;
      // AbortError is expected when media is removed or src changes — suppress it silently
      if (error?.name === 'AbortError') {
        setIsPlaying(false);
        return;
      }
      if (error?.name === 'NotSupportedError') {
        setVideoError('Format vidéo non supporté par votre navigateur');
      } else if (error?.name !== 'AbortError') {
        setVideoError('Erreur lors de la lecture vidéo');
      }
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => { if (videoRef?.current) setCurrentTime(videoRef?.current?.currentTime || 0); };
  const handleLoadedMetadata = () => { if (videoRef?.current) { setDuration(videoRef?.current?.duration || 0); setVideoError(null); } };

  const handleSeek = (e) => {
    if (!videoRef?.current || !duration) return;
    const rect = e?.currentTarget?.getBoundingClientRect();
    const time = ((e?.clientX - rect?.left) / rect?.width) * duration;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    return `${Math.floor(time / 60)}:${Math.floor(time % 60)?.toString()?.padStart(2, '0')}`;
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e?.target?.value);
    setVolume(v);
    if (videoRef?.current) videoRef.current.volume = v;
  };

  const toggleFullscreen = async () => {
    const container = videoContainerRef?.current;
    const video = videoRef?.current;
    if (!container && !video) return;

    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        // On iOS Safari, use video element's webkitEnterFullscreen
        if (video && video?.webkitEnterFullscreen) {
          video?.webkitEnterFullscreen();
          return;
        }
        const el = container || video;
        if (el?.requestFullscreen) await el?.requestFullscreen();
        else if (el?.webkitRequestFullscreen) el?.webkitRequestFullscreen();
        else if (el?.mozRequestFullScreen) el?.mozRequestFullScreen();
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        setIsFullscreen(false);
      }
    } catch (_) {}
  };

  useEffect(() => {
    const onFsChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement);
      setIsFullscreen(isFull);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    // iOS Safari fires webkitendfullscreen on the video element
    const video = videoRef?.current;
    if (video) {
      video?.addEventListener('webkitendfullscreen', () => setIsFullscreen(false));
      video?.addEventListener('webkitbeginfullscreen', () => setIsFullscreen(true));
    }
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
    };
  }, []);

  const handlePlay = () => { setIsPlaying(true); setVideoError(null); };
  const handlePause = () => {
    setIsPlaying(false);
    if (playPromiseRef?.current) { playPromiseRef?.current?.catch(() => {}); playPromiseRef.current = null; }
  };

  const handleError = () => {
    setIsPlaying(false);
    if (playPromiseRef?.current) { playPromiseRef?.current?.catch(() => {}); playPromiseRef.current = null; }
    // Only set video error for non-abort errors
    if (videoRef?.current && !videoRef?.current?.error) return;
    // Suppress AbortError — it fires when the video src changes while playing
    const mediaError = videoRef?.current?.error;
    if (mediaError && mediaError?.code === MediaError.MEDIA_ERR_ABORTED) return;
    setVideoError('Erreur de lecture vidéo');
    if (retryCount < maxRetries) setTimeout(() => retryVideoLoad(), 2000);
  };

  const nextVideo = () => {
    if (videos?.length > 1) { setCurrentVideoIndex(prev => (prev + 1) % videos?.length); setIsPlaying(false); setRetryCount(0); }
  };
  const previousVideo = () => {
    if (videos?.length > 1) { setCurrentVideoIndex(prev => (prev - 1 + videos?.length) % videos?.length); setIsPlaying(false); setRetryCount(0); }
  };

  // Determine the current video type
  const currentVideo = videos?.[currentVideoIndex];
  const currentVideoType = currentVideo?.type || (videoUrl ? (isGlbFile(videoUrl) ? 'glb' : isObjFile(videoUrl) ? 'obj' : 'video') : 'video');

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-deep h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-brand-gold/10" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-brand-gold animate-spin" />
          </div>
          <p className="text-white/60 text-sm">Chargement de la visite...</p>
        </div>
      </div>
    );
  }

  // ── No video / error ─────────────────────────────────────────────────────
  if (!videoUrl && !videoError) {
    return (
      <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-deep h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center">
        <div className="text-center px-6">
          <Icon name="Video" size={40} className="text-white/20 mx-auto mb-3" />
          <p className="text-white/40 text-sm">Aucune visite disponible pour cette propriété</p>
        </div>
      </div>
    );
  }

  if (videoError) {
    return (
      <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-deep h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center">
        <div className="text-center px-6">
          <Icon name="AlertTriangle" size={40} className="text-red-400 mx-auto mb-3" />
          <p className="text-white font-semibold mb-2">Visite indisponible</p>
          <p className="text-white/50 text-sm">{videoError}</p>
        </div>
      </div>
    );
  }

  // 3D model (OBJ or GLB/GLTF)
  if (is3DModelFile(videoUrl) || currentVideoType === 'obj' || currentVideoType === 'glb' || currentVideoType === 'gltf') {
    return (
      <ObjModelViewer
        url={videoUrl}
        property={property}
        currentVideoIndex={currentVideoIndex}
        totalVideos={videos?.length || 1}
        onPrev={() => setCurrentVideoIndex(prev => Math.max(0, prev - 1))}
        onNext={() => setCurrentVideoIndex(prev => Math.min((videos?.length || 1) - 1, prev + 1))}
      />
    );
  }

  // Embeddable video (YouTube/Vimeo)
  if (isEmbeddable(videoUrl)) {
    return (
      <IframeVideoPlayer
        url={videoUrl}
        property={property}
        currentVideoIndex={currentVideoIndex}
        totalVideos={videos?.length || 1}
        onPrev={() => setCurrentVideoIndex(prev => Math.max(0, prev - 1))}
        onNext={() => setCurrentVideoIndex(prev => Math.min((videos?.length || 1) - 1, prev + 1))}
      />
    );
  }

  // Native HTML5 video
  return (
    <div
      ref={videoContainerRef}
      className="relative bg-black rounded-xl overflow-hidden shadow-deep"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      onClick={() => setShowControls(prev => !prev)}
      style={{ overscrollBehavior: 'contain', touchAction: isPlaying ? 'none' : 'auto' }}
    >
      <video
        ref={videoRef}
        className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover"
        poster={property?.images?.[0] || '/assets/images/no_image.png'}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleError}
        onLoadStart={() => { setVideoError(null); }}
        onCanPlay={() => setVideoError(null)}
        preload="metadata"
        playsInline
        key={videoUrl}
        onAbort={() => { if (playPromiseRef?.current) { playPromiseRef?.current?.catch(() => {}); playPromiseRef.current = null; } setIsPlaying(false); }}
      >
        {videoUrl && <source src={videoUrl} />}
        Votre navigateur ne supporte pas la lecture vidéo.
      </video>

      {/* Controls overlay */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${(!isPlaying || showControls) ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={togglePlay}
            className="w-16 h-16 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full"
            disabled={!videoUrl || loading}
          >
            <Icon name={isPlaying ? "Pause" : "Play"} size={32} color="white" strokeWidth={2} />
          </Button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
          <div className="flex items-center space-x-3">
            <span className="text-white text-sm">{formatTime(currentTime)}</span>
            <div className="flex-1 h-2 bg-white/30 rounded-full cursor-pointer" onClick={handleSeek}>
              <div className="h-full bg-brand-gold rounded-full transition-all duration-150" style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }} />
            </div>
            <span className="text-white text-sm">{formatTime(duration)}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" onClick={togglePlay} className="text-white hover:bg-white/20" disabled={!videoUrl || loading}>
                <Icon name={isPlaying ? "Pause" : "Play"} size={20} />
              </Button>
              <div className="flex items-center space-x-2">
                <Icon name="Volume2" size={16} color="white" />
                <input type="range" min="0" max="1" step="0.1" value={volume} onChange={handleVolumeChange} className="w-20 h-1 bg-white/30 rounded-full appearance-none cursor-pointer" />
              </div>
              {videos?.length > 1 && (
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm" onClick={previousVideo} className="text-white hover:bg-white/20"><Icon name="SkipBack" size={16} /></Button>
                  <span className="text-white text-sm">{currentVideoIndex + 1} / {videos?.length}</span>
                  <Button variant="ghost" size="sm" onClick={nextVideo} className="text-white hover:bg-white/20"><Icon name="SkipForward" size={16} /></Button>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {videoError && retryCount < maxRetries && (
                <Button variant="ghost" size="sm" onClick={retryVideoLoad} disabled={isRetrying} className="text-white hover:bg-white/20">
                  <Icon name="RefreshCw" size={16} className={isRetrying ? 'animate-spin' : ''} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className="text-white hover:bg-white/20"
                aria-label={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
              >
                <Icon name={isFullscreen ? "Minimize" : "Maximize"} size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Top info overlay */}
      <div className="absolute top-4 left-4 right-4 pointer-events-none">
        <div className="bg-black/50 backdrop-blur-sm rounded-lg p-4">
          <h3 className="text-white font-heading font-heading-bold text-lg mb-1">Visite Virtuelle — {property?.title}</h3>
          <p className="text-white/80 text-sm">Découvrez cette propriété exceptionnelle à {property?.communes?.name}</p>
          {currentVideo?.title && <p className="text-white/70 text-xs mt-1">{currentVideo?.title}</p>}
        </div>
      </div>

      {/* Error overlay */}
      {videoError && !loading && (
        <div className="absolute bottom-20 left-4 right-4">
          <div className="bg-red-600/80 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={16} className="text-white mr-2" />
              <p className="text-white text-sm">{videoError}</p>
            </div>
            {retryCount < maxRetries && (
              <Button onClick={retryVideoLoad} disabled={isRetrying} size="xs" className="bg-white/20 hover:bg-white/30 text-white ml-2">
                <Icon name="RefreshCw" size={12} className={isRetrying ? 'animate-spin' : ''} />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyVideoPlayer;